import { useEffect } from 'react';
import { useLoaderData } from 'react-router';

import { MenuHeader } from '@jane/bloom/components/menuHeader/menuHeader';
import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { useMenuTopRowWithServerData } from '@jane/bloom/hooks/dm/useMenuTopRowWithServerData';
import { BloomFilterProvider } from '@jane/bloom/hooks/filters';
import type { CustomRowResults } from '@jane/bloom/loaders/routes/customRow.server';
import { trackSearchedMenuProducts } from '@jane/bloom/services/tracking/track';

import { CustomRowDetails } from './components/customRowDetails';

export function CustomRowRouteComponent() {
  const {
    availableFilters,
    brandSpecials,
    customLanguage,
    customRow,
    enabledSpecialsForRow,
    janeGold,
    menuProductImgsAboveTheFold,
    menuTopRow,
    recommendedSortResults,
    searchCriteria,
    searchFilterWithoutBrands,
    sort: { currentSort, sortOptions },
    storeId,
  } = useLoaderData<CustomRowResults>();

  const rowInstance = useMemoizedRowInstance(recommendedSortResults);

  // Handles server-side initial load and client-side refetching of the menu top row
  const {
    pauseRefetching,
    rowInstance: menuTopRowInstance,
    unpauseRefetching,
  } = useMenuTopRowWithServerData({
    initialMenuTopRow: menuTopRow,
    searchFilter: searchFilterWithoutBrands,
    storeId,
  });

  useEffect(() => {
    if (!rowInstance) return;
    trackSearchedMenuProducts({
      hits: rowInstance?.totalProducts,
      searchCriteria,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(searchCriteria), rowInstance?.totalProducts]);

  return (
    <BloomFilterProvider
      availableFilters={availableFilters}
      searchCriteria={searchCriteria}
    >
      <MenuHeader />
      <CustomRowDetails
        pauseRefetching={pauseRefetching}
        unpauseRefetching={unpauseRefetching}
        brandSpecials={brandSpecials}
        customLanguage={customLanguage}
        customRow={customRow}
        currentSort={currentSort}
        specials={enabledSpecialsForRow}
        menuTopRowInstance={menuTopRowInstance}
        preloadedProductIds={menuProductImgsAboveTheFold.map(({ id }) => id)}
        sortOptions={sortOptions}
        rowInstance={rowInstance}
        janeGold={janeGold}
      />
    </BloomFilterProvider>
  );
}
